import { useEffect, useState } from "react";
import axios from "axios";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "./ui/sheet";
import { useBooking } from "../context/BookingContext";
import { Check, ArrowRight, ArrowLeft, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const services = [
  "Bridal Hair & Makeup",
  "Editorial Bridal Trial",
  "Evening Glamour Styling",
  "Signature Scalp Ritual",
  "Botanical Detox Treatment",
  "Holistic Scalp Consultation",
];

const times = ["9:00 AM", "11:00 AM", "1:00 PM", "3:00 PM", "5:00 PM"];

const lineInput =
  "w-full bg-transparent border-0 border-b border-obsidian/20 focus:border-champagne outline-none py-3 text-obsidian font-light text-base placeholder:text-obsidian/30 transition-colors duration-300";

const emptyForm = {
  service: "",
  name: "",
  email: "",
  phone: "",
  preferred_date: "",
  preferred_time: "",
  notes: "",
};

export const BookingDrawer = () => {
  const { open, setOpen, presetService } = useBooking();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setStep(0);
      setConfirmed(false);
      setError("");
      setForm({ ...emptyForm, service: presetService || "" });
    }
  }, [open, presetService]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canNext =
    (step === 0 && form.service) ||
    (step === 1 && form.name && form.email && form.phone) ||
    (step === 2 && form.preferred_date && form.preferred_time);

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/bookings`, form);
      setConfirmed(true);
    } catch (e) {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const stepsMeta = [
    { label: "Service" },
    { label: "Details" },
    { label: "Schedule" },
  ];

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="right"
        data-testid="booking-drawer"
        className="w-full sm:max-w-[450px] bg-[#FDFBF7] border-l border-[#E8E2D2] p-0 overflow-y-auto"
      >
        <div className="p-8 md:p-10 min-h-full flex flex-col">
          <SheetTitle className="sr-only">Secure a Consultation with Picaso Studios</SheetTitle>
          <SheetDescription className="sr-only">
            Complete the multi-step form to request your bridal hair, makeup, or scalp wellness consultation.
          </SheetDescription>
          {/* Header */}
          <div className="mb-10">
            <p className="text-umber text-xs uppercase tracking-[0.3em] mb-3">Picaso Studios</p>
            <h3 className="font-serif font-light text-3xl text-obsidian">
              {confirmed ? "You're booked" : "Secure a Consultation"}
            </h3>
          </div>

          {!confirmed && (
            <>
              {/* Progress */}
              <div className="flex items-center gap-2 mb-12">
                {stepsMeta.map((s, i) => (
                  <div key={s.label} className="flex-1">
                    <div
                      className={`h-px transition-all duration-500 ${
                        i <= step ? "bg-champagne" : "bg-obsidian/15"
                      }`}
                    />
                    <p
                      className={`mt-3 text-[10px] uppercase tracking-[0.15em] transition-colors ${
                        i <= step ? "text-obsidian" : "text-obsidian/30"
                      }`}
                    >
                      0{i + 1} {s.label}
                    </p>
                  </div>
                ))}
              </div>

              {/* Step content */}
              <div className="flex-1">
                {step === 0 && (
                  <div data-testid="booking-step-service">
                    <p className="font-light text-sm text-obsidian/60 mb-6 leading-[1.6]">
                      Which experience are you drawn to?
                    </p>
                    <div className="space-y-px">
                      {services.map((s) => (
                        <button
                          key={s}
                          data-testid={`booking-service-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                          onClick={() => set("service", s)}
                          className={`w-full text-left px-5 py-4 border transition-all duration-300 font-light text-sm ${
                            form.service === s
                              ? "border-champagne bg-champagne/10 text-obsidian"
                              : "border-obsidian/10 text-obsidian/70 hover:border-obsidian/30"
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {step === 1 && (
                  <div data-testid="booking-step-details" className="space-y-8">
                    <div>
                      <label className="text-xs uppercase tracking-[0.15em] text-obsidian/50">Full Name</label>
                      <input
                        data-testid="booking-input-name"
                        className={lineInput}
                        placeholder="Your name"
                        value={form.name}
                        onChange={(e) => set("name", e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-[0.15em] text-obsidian/50">Email</label>
                      <input
                        data-testid="booking-input-email"
                        type="email"
                        className={lineInput}
                        placeholder="you@email.com"
                        value={form.email}
                        onChange={(e) => set("email", e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-[0.15em] text-obsidian/50">Phone</label>
                      <input
                        data-testid="booking-input-phone"
                        className={lineInput}
                        placeholder="+1 (000) 000-0000"
                        value={form.phone}
                        onChange={(e) => set("phone", e.target.value)}
                      />
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div data-testid="booking-step-schedule" className="space-y-8">
                    <div>
                      <label className="text-xs uppercase tracking-[0.15em] text-obsidian/50">Preferred Date</label>
                      <input
                        data-testid="booking-input-date"
                        type="date"
                        className={lineInput}
                        value={form.preferred_date}
                        onChange={(e) => set("preferred_date", e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-[0.15em] text-obsidian/50 block mb-4">
                        Preferred Time
                      </label>
                      <div className="grid grid-cols-3 gap-2">
                        {times.map((t) => (
                          <button
                            key={t}
                            data-testid={`booking-time-${t.replace(/[^0-9a-zA-Z]+/g, "-")}`}
                            onClick={() => set("preferred_time", t)}
                            className={`py-3 text-xs border transition-all duration-300 ${
                              form.preferred_time === t
                                ? "border-champagne bg-champagne/10 text-obsidian"
                                : "border-obsidian/10 text-obsidian/60 hover:border-obsidian/30"
                            }`}
                          >
                            {t}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-[0.15em] text-obsidian/50">Notes (optional)</label>
                      <textarea
                        data-testid="booking-input-notes"
                        rows={3}
                        className={`${lineInput} resize-none`}
                        placeholder="Tell us about your vision..."
                        value={form.notes}
                        onChange={(e) => set("notes", e.target.value)}
                      />
                    </div>
                  </div>
                )}
              </div>

              {error && (
                <p data-testid="booking-error" className="text-sm text-red-600 mt-6">{error}</p>
              )}

              {/* Nav buttons */}
              <div className="flex items-center justify-between gap-4 mt-12 pt-6 border-t border-obsidian/10">
                {step > 0 ? (
                  <button
                    data-testid="booking-back-button"
                    onClick={() => setStep((s) => s - 1)}
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-obsidian/60 hover:text-obsidian transition-colors"
                  >
                    <ArrowLeft size={16} /> Back
                  </button>
                ) : (
                  <span />
                )}

                {step < 2 ? (
                  <button
                    data-testid="booking-next-button"
                    disabled={!canNext}
                    onClick={() => setStep((s) => s + 1)}
                    className="inline-flex items-center gap-3 bg-obsidian text-[#FDFBF7] px-7 py-3.5 text-xs uppercase tracking-[0.2em] disabled:opacity-30 disabled:cursor-not-allowed hover:bg-umber transition-all duration-400"
                  >
                    Continue <ArrowRight size={16} />
                  </button>
                ) : (
                  <button
                    data-testid="booking-submit-button"
                    disabled={!canNext || submitting}
                    onClick={submit}
                    className="inline-flex items-center gap-3 bg-obsidian text-[#FDFBF7] px-7 py-3.5 text-xs uppercase tracking-[0.2em] disabled:opacity-30 disabled:cursor-not-allowed hover:bg-umber transition-all duration-400"
                  >
                    {submitting ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                    Confirm Booking
                  </button>
                )}
              </div>
            </>
          )}

          {confirmed && (
            <div data-testid="booking-confirmation" className="flex-1 flex flex-col">
              <div className="w-16 h-16 rounded-full border border-champagne flex items-center justify-center mb-8">
                <Check size={26} className="text-champagne" />
              </div>
              <p className="font-light text-base text-obsidian/80 leading-[1.6] max-w-sm">
                Thank you, <span className="text-obsidian">{form.name.split(" ")[0]}</span>. Your
                request for <span className="text-obsidian italic font-serif text-lg">{form.service}</span> on{" "}
                <span className="text-obsidian">{form.preferred_date}</span> at{" "}
                <span className="text-obsidian">{form.preferred_time}</span> has been received.
              </p>
              <p className="mt-6 font-light text-sm text-obsidian/60 leading-[1.6]">
                Our atelier will reach out within 24 hours to finalise the details of your
                consultation.
              </p>
              <div className="mt-auto pt-10">
                <button
                  data-testid="booking-done-button"
                  onClick={() => setOpen(false)}
                  className="w-full bg-obsidian text-[#FDFBF7] py-4 text-xs uppercase tracking-[0.2em] hover:bg-umber transition-colors duration-400"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default BookingDrawer;
